<template>
  <div :class="$style.page">
    <div :class="$style.page__container">
      <h1 :class="$style.page__title">次回面談予約</h1>

      <section :class="[$style.page__section, $style.section]">
        <h2 :class="$style.section__title">前回の予約名</h2>
        <div :class="$style.section__body">
          <LoadingIndicator v-if="!maskedPreviousBookingSummary" />
          <p v-else>
            {{ maskedPreviousBookingSummary }}
          </p>
        </div>
      </section>

      <section :class="[$style.page__section, $style.section]">
        <h2 :class="$style.section__title">次回予約カレンダー</h2>
        <div :class="$style.section__body">
          <LoadingIndicator v-if="!calendarResult" />
          <p v-else-if="calendarPublicTitle">
            {{ calendarPublicTitle }}
          </p>
          <p v-else :class="$style.section__error">
            カレンダー情報の取得に失敗しました
          </p>
        </div>
      </section>

      <section :class="[$style.page__section, $style.section]">
        <div v-if="selectedTimeslot" :class="$style.section__body">
          <NextBookingComplete
            v-if="isBookingCompleted"
            :timeSlot="selectedTimeslot"
          />
          <NextBookingConfirmation
            v-else
            :timeSlot="selectedTimeslot"
            :isSubmitting="isSubmitting"
            @confirm="handleSubmit"
            @cancel="handleCancel"
          />
        </div>

        <div v-else>
          <h2 :class="$style.section__title">日時を選ぶ</h2>
          <TimeSlotPicker
            v-if="calendarPublicTitle"
            :class="$style.section__body"
            v-model="selectedTimeslot"
            :calendar-guid="calendarGUID"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'

import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useAlert } from '@/libs/alertPortal'
import { captureException } from '@/newrelic'

import {
  useFetchPreviousBookingQuery,
  useFetchCalendarDetailForNextBookingQuery,
  useNextBookWithCalendarByEntranceMutation,
} from './NextBookingPage.generated'
import NextBookingConfirmation from './NextBookingConfirmation.vue'
import NextBookingComplete from './NextBookingComplete.vue'
import type { TimeSlot } from './TimeSlotPicker/useTimeSlot'
import TimeSlotPicker from './TimeSlotPicker/TimeSlotPicker.vue'
import { useRoute } from 'vue-router'
import { APIError } from '@/libs/utilities/CustomErrors'
import { redactUserNameInBookingSummary } from './util'

const alert = useAlert()

const route = useRoute('nextBooking')
const previousBookingGUID = route.params.previousBookingGUID
const calendarGUID = route.params.calendarGUID

const { result: previousBookingResult, error: previousBookingError } =
  useFetchPreviousBookingQuery({
    guid: previousBookingGUID,
  })
const { result: calendarResult, error: calendarError } =
  useFetchCalendarDetailForNextBookingQuery({ guid: calendarGUID })

watchEffect(() => {
  if (previousBookingError.value || calendarError.value) {
    throw new APIError(500, 'エラーが発生しました')
  }

  if (
    calendarResult.value?.calendarDetail.__typename ===
      'CalendarDetailPermissionDenied' ||
    previousBookingResult.value?.bookingV2.__typename ===
      'BookingErrorPermissionDenined'
  ) {
    throw new APIError(403, 'この予約URLの閲覧許可がありません')
  }

  if (
    calendarResult.value?.calendarDetail.__typename === 'CalendarDetailNotFound'
  ) {
    throw new APIError(404, 'カレンダーが見つかりません')
  }

  if (
    previousBookingResult.value?.bookingV2.__typename === 'BookingErrorNotFound'
  ) {
    throw new APIError(404, '前回予約が見つかりません')
  }
})

const maskedPreviousBookingSummary = computed(() => {
  if (previousBookingResult.value?.bookingV2.__typename !== 'Booking') {
    return null
  }

  if (!previousBookingResult.value.bookingV2.summary) {
    return null
  }

  return redactUserNameInBookingSummary(
    previousBookingResult.value.bookingV2.summary
  )
})

const calendarPublicTitle = computed(() => {
  if (
    calendarResult.value?.calendarDetail.__typename === 'CalendarDetailSuccess'
  ) {
    return calendarResult.value.calendarDetail.calendar.publicTitle
  }
  return null
})

const selectedTimeslot = ref<TimeSlot | undefined>()

const { mutate, loading: isSubmitting } =
  useNextBookWithCalendarByEntranceMutation()

const isBookingCompleted = ref(false)
const handleSubmit = async () => {
  if (!selectedTimeslot.value) return

  try {
    const result = await mutate({
      calendarGUID: calendarGUID,
      previousBookingGUID: previousBookingGUID,
      startsAt: selectedTimeslot.value.startsAt,
      endsAt: selectedTimeslot.value.endsAt,
    })

    if (
      result?.data?.bookWithCalendarByEntrance?.__typename ===
      'BookWithCalendarByEntranceErrorAlreadyReserved'
    ) {
      alert.error('すでに埋まっている日程です')
      return
    }

    if (
      result?.data?.bookWithCalendarByEntrance?.__typename ===
      'BookWithCalendarByEntranceSuccess'
    ) {
      alert.success('予約が完了しました')
      isBookingCompleted.value = true
    }
  } catch (e) {
    captureException(e)
    alert.error('予約の作成に失敗しました')
    selectedTimeslot.value = undefined
  }
}

const handleCancel = () => {
  selectedTimeslot.value = undefined
}
</script>

<style lang="scss" module>
.page {
  align-self: flex-start;
  min-height: 100%;
  min-width: 800px;

  &__container {
    margin: 0 auto;
    background-color: $color-surface-primary;
    border-radius: $border-radius-inner;
    padding: 32px;
  }

  &__title {
    font-size: 24px;
    font-weight: bold;
  }

  &__section {
    margin-top: 24px;
  }
}

.section {
  &__title {
    font-size: 18px;
    font-weight: bold;
  }

  &__body {
    margin-top: 8px;
  }

  &__error {
    color: $color-text-and-object-accent-red;
  }
}
</style>
